import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search } from "lucide-react";
import { getDepartmentName, getMunicipalityName } from "@pahcertificar/certmanager-locations";
import { useSubscribers } from "@/hooks/queries";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card } from "@/components/ui/Card";
import { EmptyState, TBody, TD, TH, THead, TR, Table } from "@/components/ui/Table";
import { CenteredSpinner } from "@/components/ui/Spinner";
import { ErrorState } from "@/components/ui/ErrorState";
import { Badge } from "@/components/ui/Badge";
import { formatDate } from "@/lib/utils";

export function SubscribersList() {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [showArchived, setShowArchived] = useState(false);

  const query = useSubscribers({
    page,
    pageSize,
    search: search || undefined,
    includeArchived: showArchived || undefined,
  });

  function onSearch(e: React.FormEvent) {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  }

  const total = query.data?.total ?? 0;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Subscribers</h1>
          <p className="text-sm text-slate-500 mt-1">
            Juridica and natural persons registered in the system.
          </p>
        </div>
        <Button onClick={() => navigate("/subscribers/new")}>
          <Plus className="h-4 w-4" /> New subscriber
        </Button>
      </div>

      <form onSubmit={onSearch} className="flex items-center gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="h-3.5 w-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
          <Input
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by name, document, or email"
            className="pl-8"
          />
        </div>
        <Button variant="outline" size="md" type="submit">
          Search
        </Button>
        {search && (
          <Button
            variant="ghost"
            size="md"
            type="button"
            onClick={() => {
              setSearch("");
              setSearchInput("");
            }}
          >
            Clear
          </Button>
        )}
        <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer select-none ml-auto">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => {
              setPage(1);
              setShowArchived(e.target.checked);
            }}
            className="h-3.5 w-3.5 rounded border-slate-300 text-brand-600 focus:ring-brand-500"
          />
          Show archived
        </label>
      </form>

      <Card className="overflow-hidden">
        {query.isPending ? (
          <CenteredSpinner label="Loading subscribers…" />
        ) : query.isError ? (
          <ErrorState error={query.error} onRetry={() => query.refetch()} />
        ) : query.data.data.length === 0 ? (
          <EmptyState
            title={search ? "No results" : "No subscribers yet"}
            description={
              search
                ? "Try a different search term."
                : "Create your first subscriber to start issuing certificates."
            }
            action={
              !search && (
                <Button onClick={() => navigate("/subscribers/new")}>
                  <Plus className="h-4 w-4" /> New subscriber
                </Button>
              )
            }
          />
        ) : (
          <>
            <Table>
              <THead>
                <tr>
                  <TH>Name</TH>
                  <TH>Type</TH>
                  <TH>Document</TH>
                  <TH>Email</TH>
                  <TH>Location</TH>
                  <TH>Created</TH>
                </tr>
              </THead>
              <TBody>
                {query.data.data.map((s) => (
                  <TR
                    key={s.id}
                    onClick={() => navigate(`/subscribers/${s.id}`)}
                    className="cursor-pointer"
                  >
                    <TD>
                      <p className="font-medium text-slate-900">{s.name}</p>
                      <p className="text-[11px] text-slate-500 font-mono">{s.id}</p>
                    </TD>
                    <TD>
                      <div className="flex items-center gap-1.5">
                        <Badge tone={s.type === "juridica" ? "brand" : "info"}>{s.type}</Badge>
                        {s.status === "archived" && <Badge tone="warning">Archived</Badge>}
                      </div>
                    </TD>
                    <TD>
                      <p className="text-slate-900">
                        {s.documentType} {s.documentNumber}
                        {s.verificationDigit ? `-${s.verificationDigit}` : ""}
                      </p>
                    </TD>
                    <TD>{s.email}</TD>
                    <TD>
                      {getMunicipalityName(s.city) ?? s.city}
                      {", "}
                      {getDepartmentName(s.state) ?? s.state}
                    </TD>
                    <TD className="text-[11px] text-slate-500">{formatDate(s.createdAt)}</TD>
                  </TR>
                ))}
              </TBody>
            </Table>
            <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
              <p className="text-xs text-slate-600">
                {total} total · page {page} of {pageCount}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page === 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= pageCount}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </div>
  );
}
